// Search for the sidebar: type what you want to do, in your own words
// ("change my password", "add a staff member", "whatsapp", "شعار"), and get
// the page (and the part of it) that does it. Everything runs in the browser
// from the list below; no data leaves the page.

/** One thing people look for. key is the sidebar page it lives on: results
 *  only show for pages this person can open. */
export interface Feature {
  key: string;
  title: string;
  /** Path after /o/<org>; may carry a query (?sync=1). */
  path: string;
  /** A heading on the page to scroll to. */
  find?: string;
  /** Other ways people say it (English and Arabic). */
  words: string;
  /** A permission the page needs beyond seeing key (useOrg().allows). */
  allow?: string;
  /** The page itself: shown (and found) under the company's own name for it. */
  page?: boolean;
}

export const features: Feature[] = [
  // Today
  { key: "today", page: true, title: "Today", path: "", words: "home dashboard overview summary agenda schedule start main الرئيسية اليوم لوحة" },

  // Sales
  { key: "contacts", page: true, title: "Customers", path: "/contacts", words: "contacts clients customers patients leads people crm phonebook address book list عملاء العملاء زبائن جهات اتصال" },
  { key: "contacts", allow: "import", title: "Import customers from a file", path: "/contacts/import", words: "import upload excel xlsx csv spreadsheet vcard vcf phone contacts migrate bring in move from another crm استيراد ملف اكسل" },
  { key: "contacts", title: "Export or erase a customer's data", path: "/contacts", words: "export download customer data copy erase delete customer remove personal data privacy pdpl gdpr request حذف بيانات" },
  { key: "deals", page: true, title: "Deals", path: "/deals", words: "deals opportunities pipeline sales prospects forecast won lost صفقات مبيعات فرص" },
  { key: "deals", allow: "pipelines", title: "Pipelines and stages", path: "/deals/pipelines", words: "pipeline pipelines stages sales process funnel columns kanban setup مراحل" },
  { key: "tasks", page: true, title: "Tasks", path: "/tasks", words: "tasks todo to-do follow up followup reminders to do list chores مهام متابعة" },
  { key: "results", page: true, title: "Results & ROI", path: "/results", words: "results reports report analytics roi return revenue performance statistics stats numbers kpi insights how is the ai doing تقارير نتائج احصائيات" },

  // Conversations
  { key: "inbox", page: true, title: "Inbox", path: "/inbox", words: "inbox messages conversations chats replies reply unread handoff handover live chat talk to customer رسائل محادثات البريد الوارد" },
  { key: "channels", title: "WhatsApp and website chat", path: "/channels", words: "channels whatsapp connect number meta business phone widget website chat embed واتساب واتس قنوات" },
  { key: "channels", title: "WhatsApp templates", path: "/channels", find: "WhatsApp", words: "whatsapp templates template messages approved follow up outside 24 hours broadcast قوالب" },
  { key: "email", title: "Email mailboxes", path: "/settings/email", words: "email mail connect outlook gmail microsoft google mailbox inbox account بريد ايميل" },
  { key: "email", title: "Saved emails (templates)", path: "/settings/email", find: "Saved emails", words: "email templates saved emails canned replies signature قوالب البريد" },
  { key: "email", title: "Automated emails", path: "/settings/email", find: "Automated emails", words: "automated emails booking confirmation reminder email notifications" },

  // Bookings
  { key: "calendar", page: true, title: "Calendar", path: "/calendar", words: "calendar appointments bookings schedule reservations diary agenda week day new booking add booking book appointment تقويم مواعيد حجوزات" },
  { key: "calendar", title: "Connect Google or Outlook calendar", path: "/calendar?sync=1", words: "calendar sync google calendar outlook calendar connect sync two way busy times integrate مزامنة" },
  { key: "services", page: true, title: "Services", path: "/services", words: "services treatments offerings products menu prices price list pricing cost duration length what we offer add service خدمات الخدمات اسعار" },
  { key: "services", title: "Staff and rooms", path: "/services", find: "rooms", words: "staff rooms resources doctors stylists therapists who offers chairs tables people bookable موظفين غرف" },
  { key: "services", title: "Opening hours", path: "/services", find: "Opening hours", words: "opening hours working hours availability open close shifts timings business hours ساعات العمل الدوام" },
  { key: "services", title: "Holidays and time off", path: "/services", find: "Holidays", words: "holidays time off vacation leave closed days day off closure اجازة عطلة" },
  { key: "services", title: "Booking rules", path: "/services", find: "Booking rules", words: "booking rules notice how far ahead minimum notice buffer cancellation policy قواعد الحجز" },
  { key: "booking-page", page: true, title: "Booking page", path: "/booking-page", words: "booking page online booking link public page book online embed website schedule link url share صفحة الحجز رابط" },

  // AI & automation
  { key: "agents", page: true, title: "AI agents", path: "/agents", words: "ai agents bot chatbot assistant gpt virtual receptionist auto reply answer customers ذكاء اصطناعي بوت مساعد" },
  { key: "agents", title: "Create an AI agent", path: "/agents/new", words: "new agent create bot build assistant set up ai" },
  { key: "knowledge", page: true, title: "Knowledge", path: "/knowledge", words: "knowledge faq questions answers documents files pdf website pages training info teach the ai saved answers معلومات معرفة" },
  { key: "automations", page: true, title: "Automations", path: "/automations", words: "automations workflows rules triggers automatic reminders appointment reminder follow ups notify team daily schedule email zap اتمتة تذكير" },
  { key: "automations", title: "New automation", path: "/automations/new", words: "new automation create rule when this happens do that reminder" },

  // Company
  { key: "team", page: true, title: "Team", path: "/team", words: "team staff employees users members colleagues invite add user add employee roles permissions access remove فريق موظفين صلاحيات" },
  { key: "team", title: "Invite someone", path: "/team", find: "Invite someone", words: "invite add staff add employee new user add member دعوة" },
  { key: "workspace", page: true, title: "Fields & words", path: "/settings/workspace", words: "custom fields fields words rename labels terminology call customers patients names حقول" },
  { key: "brand", page: true, title: "Brand", path: "/settings/brand", words: "brand logo colours colors colour theme font look branding style شعار الوان" },

  // Settings
  { key: "settings", title: "Company details", path: "/settings", find: "Company", words: "company details name business name address phone website email info settings general اعدادات الشركة" },
  { key: "settings", title: "Sidebar: order, folders, hide tabs", path: "/settings/sidebar", words: "sidebar order folders hide tabs menu rearrange customise customize reorder show hidden tabs القائمة" },
  { key: "settings", title: "Plan and usage", path: "/settings/usage", words: "plan usage limits subscription billing package how many left quota الباقة الاشتراك" },
  { key: "settings", title: "Close the company", path: "/settings", find: "Close", words: "close company delete company cancel account shut down" },
  { key: "settings", title: "Your account and password", path: "/settings/account", words: "password change password my account profile email address sign in two factor 2fa authenticator app recovery codes sessions sign out everywhere delete my account كلمة المرور كلمة السر حسابي" },
  { key: "team", title: "Company security", path: "/settings/security", words: "security two factor for everyone 2fa sessions staying signed in allowed networks ip office vpn google sign in support access security log audit retention delete old conversations امان حماية" },
];

const stop = new Set("a an the to of for in on at my our me i we you how do does can could would want need please where is are be with and or from it this that what which find go show see view look check get let know am there any some up".split(" "));

/** normalize lowercases, drops accents and punctuation, and evens out Arabic letter forms. */
export function normalize(s: string): string {
  return s
    .toLowerCase()
    .normalize("NFKD").replace(/[̀-ͯ]/g, "")
    .replace(/[ً-ٰٟـ]/g, "")
    .replace(/[أإآ]/g, "ا").replace(/ى/g, "ي").replace(/ة/g, "ه").replace(/^ال(?=\S{3})/g, "")
    .replace(/[^\p{L}\p{N}]+/gu, " ")
    .trim();
}

function stem(w: string): string {
  if (/[؀-ۿ]/.test(w)) return w.replace(/^ال(?=\S{3})/, "");
  if (w.length > 5 && w.endsWith("ing")) return w.slice(0, -3);
  if (w.length > 4 && w.endsWith("ies")) return w.slice(0, -3) + "y";
  if (w.length > 4 && (w.endsWith("es") && /(ches|shes|xes|sses)$/.test(w))) return w.slice(0, -2);
  if (w.length > 3 && w.endsWith("s") && !w.endsWith("ss")) return w.slice(0, -1);
  if (w.length > 4 && w.endsWith("ed")) return w.slice(0, -2);
  return w;
}

function words(s: string): string[] {
  return normalize(s).split(" ").filter(Boolean).map(stem);
}

/** distance is the edit distance between two short words, capped at max+1. */
function distance(a: string, b: string, max: number): number {
  if (Math.abs(a.length - b.length) > max) return max + 1;
  let prev = Array.from({ length: b.length + 1 }, (_, i) => i);
  for (let i = 1; i <= a.length; i++) {
    const cur = [i];
    let best = i;
    for (let j = 1; j <= b.length; j++) {
      cur[j] = Math.min(prev[j] + 1, cur[j - 1] + 1, prev[j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1));
      best = Math.min(best, cur[j]);
    }
    if (best > max) return max + 1;
    prev = cur;
  }
  return prev[b.length];
}

function tokenScore(q: string, w: string): number {
  if (q === w) return 3;
  if (w.startsWith(q) && q.length >= 2) return 2.2;
  if (q.length >= 4 && q.startsWith(w) && w.length >= 4) return 1.6;
  const max = q.length >= 8 ? 2 : q.length >= 5 ? 1 : 0;
  if (max && distance(q, w, max) <= max) return 1.4;
  return 0;
}

interface Indexed { f: Feature; title: string[]; body: string[] }
const index: Indexed[] = features.map((f) => ({ f, title: words(f.title), body: words(f.words) }));

export interface Hit { feature: Feature; score: number }

/** search ranks features for what was typed. labels are this company's own
 *  names for pages (e.g. "Patients" for contacts), searched as titles too. */
export function search(query: string, allowed: Set<string>, labels: Map<string, string> = new Map(), limit = 8, can: (perm: string) => boolean = () => true): Hit[] {
  const all = words(query);
  const q = all.filter((w) => !stop.has(w));
  const terms = q.length ? q : all;
  if (!terms.length) return [];
  const hits: Hit[] = [];
  for (const it of index) {
    if (!allowed.has(it.f.key) || (it.f.allow && !can(it.f.allow))) continue;
    const own = labels.get(it.f.key);
    const title = own && it.f.page ? [...it.title, ...words(own)] : it.title;
    let score = 0, matched = 0, strong = false;
    for (const t of terms) {
      let best = 0, raw = 0;
      for (const w of title) { const v = tokenScore(t, w); raw = Math.max(raw, v); best = Math.max(best, v * 1.5); }
      for (const w of it.body) { const v = tokenScore(t, w); raw = Math.max(raw, v); best = Math.max(best, v); }
      if (best > 0) matched++;
      if (raw >= 2.2) strong = true;
      score += best;
    }
    // Most of what was typed has to match; a whole-phrase match in the title wins.
    if (matched === 0 || matched / terms.length < 0.5) continue;
    if (normalize(it.f.title).includes(normalize(query)) && query.trim().length > 2) score += 3;
    score *= matched / terms.length;
    // A lone near-miss (one fuzzy word out of several) isn't an answer.
    if (score < 1.8 && !strong) continue;
    hits.push({ feature: it.f, score });
  }
  hits.sort((a, b) => b.score - a.score || a.feature.title.localeCompare(b.feature.title));
  // Drop weak side matches once there's a clearly better answer.
  const top = hits[0]?.score ?? 0;
  return hits.filter((h) => h.score >= top * 0.5).slice(0, limit);
}
